import styles from './ResultList.module.css';
import {
  Card,
  Checkbox,
  Dropdown,
  Flex,
  Icon,
  Pill,
  Separator,
  Text,
} from '@/_components';
import { getClasses } from '@/_utils/classnames';
import { ChangeEvent, MouseEvent, useMemo, type FC } from 'react';
import { ColorType } from '@/_types/colors';
import type { ResultListItem, ResultListProps } from './ResultList.types';

export const ResultList: FC<ResultListProps> = ({
  card = true,
  items,
  itemRole = 'listitem',
  onSelect,
  multipleSelect = false,
}) => {
  const filteredItems: Array<ResultListItem> = useMemo(
    () => items?.filter((i) => !i.isHidden),
    [items],
  );

  if (!filteredItems?.length) return null;

  const getTextColor = (item: ResultListItem): ColorType => {
    if (item?.isDisabled) return 'disabled';
    if (item.type === 'category') return 'disabled';
    if (item.color) return item.color;
    if (item.isSecondary) return 'secondary';

    return 'primary';
  };

  const results = filteredItems.map((item, key) => {
    const onItemClick = (event?: MouseEvent<HTMLDivElement>) => {
      if (item.isDisabled) return;

      item.onClick?.(item);

      // If the item has nested dropdown items, stop the event from dismissing the parent dropdown(s)
      if (item.items) {
        event?.stopPropagation();
      } else {
        onSelect?.(item);
      }
    };

    const resultListItemType = item.type === 'category' ? 'micro' : 'small';
    const resultListItemClasses: string = getClasses(styles.resultListItem, {
      [styles.resultListItemCategory]: item.type === 'category',
      [styles.resultListItemSeparator]: item.type === 'separator',
      [styles.resultListItemIsDisabled]: !!item.isDisabled,
      [styles.resultListItemIsActive]: item.isActive,
    });

    const iconColor: ColorType | undefined =
      item.icon === 'check-circle-filled'
        ? 'success'
        : item.isDisabled
          ? 'disabled'
          : item.color === 'critical'
            ? 'critical'
            : undefined;
    const textColor: ColorType = getTextColor(item);
    const labelType = item.labelType ?? 'warning';

    const isCheck: boolean =
      item.icon === 'check-circle-filled' || item.icon === 'circle';

    const onCheckToggle = (_event: ChangeEvent<HTMLDivElement>) => {
      return item.onClick?.(item);
    };

    const resultListItem = (
      <Flex
        data-testid="result-list-item"
        key={key}
        className={resultListItemClasses}
        onClick={onItemClick}
        role={itemRole}
        isFullWidth
        gap={4}
        aria-label={item.name}
      >
        <Flex
          gap={2}
          isGrowing
          isFullWidth
          isColumn={item.type === 'category'}
          xAlign={item.type === 'category' ? 'start' : undefined}
          isYCentered={item.type !== 'category'}
        >
          {!isCheck && item.icon && (
            <Icon size={20} name={item.icon} color={iconColor} />
          )}
          {isCheck && multipleSelect && (
            <Checkbox
              isChecked={item.icon === 'check-circle-filled'}
              onChange={onCheckToggle}
              label={item.name!}
              isLabelHidden
            />
          )}
          {item.name && (
            <Text type={resultListItemType} color={textColor}>
              {item.name}
            </Text>
          )}
          {item.label && (
            <Pill label={item.label} size="small" intent={labelType} isRound />
          )}
          {(item.type === 'separator' || item.type === 'category') && (
            <Separator isLight />
          )}
        </Flex>
        {item.items && <Icon name="caret-right" size={16} color="secondary" />}
      </Flex>
    );

    if (item.items) {
      return (
        <Dropdown
          key={key}
          items={item.items}
          onSelect={onSelect}
          placement="right-start"
        >
          {resultListItem}
        </Dropdown>
      );
    }

    return resultListItem;
  });

  if (card) {
    return (
      <Card className={styles.resultList} elevation="md" padding={2} gap={2}>
        {results}
      </Card>
    );
  }

  return results;
};
